<template>
  <portlet :icon="'fal fa-handshake'" :title="'Klantbezoeken'" :actionsBlock="false">
    <template slot="portletBody">
      <div class="row dataTable-filters mb-5">
        <div class="col-md-4 col-xl-2">
          <div class="dropdown">
            <button class="btn btn-primary dropdown-toggle" type="button" data-toggle="dropdown">
              <span>Actief ({{ activeFilter === null ? 'Alles' : activeFilter ? 'Ja' : 'Nee' }})</span>
            </button>
            <div class="dropdown-menu">
              <div class="option-wrapper">
                <div class="custom-control custom-radio">
                  <input type="radio" class="custom-control-input" id="filter-active-all" :value="null" v-model="activeFilter" />
                  <label class="custom-control-label" for="filter-active-all"><span>Alles</span></label>
                </div>
                <div class="custom-control custom-radio">
                  <input type="radio" class="custom-control-input" id="filter-active-yes" :value="true" v-model="activeFilter" />
                  <label class="custom-control-label" for="filter-active-yes"><span>Actief</span></label>
                </div>
                <div class="custom-control custom-radio">
                  <input type="radio" class="custom-control-input" id="filter-active-no" :value="false" v-model="activeFilter" />
                  <label class="custom-control-label" for="filter-active-no"><span>Niet actief</span></label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-if="isAdmin">
          <div v-if="activeFilter !== false" class="col-md-4 col-xl-3">
            <div class="input-group">
              <input type="number" min="1" class="form-control" v-model.lazy.number="recentThresholdWeeks" style="max-width:80px;" />
              <div class="input-group-append"><span class="input-group-text threshold-append">weken actief</span></div>
            </div>
          </div>
          <div v-if="activeFilter !== true" class="col-md-4 col-xl-3">
            <div class="input-group">
              <input type="number" min="0" class="form-control" v-model.lazy.number="inactiveThresholdWeeks" style="max-width:80px;" />
              <div class="input-group-append"><span class="input-group-text threshold-append">weken inactief</span></div>
            </div>
          </div>
        </template>
      </div>

      <table class="table visits-overview-table">
        <thead>
          <tr>
            <th style="width:32px"></th>
            <th class="sortable" @click="setSort('name')">
              Klant
              <i class="fal ml-1" :class="sortIcon('name')"></i>
            </th>
            <th class="sortable" @click="setSort('date')">
              Laatste bezoek
              <i class="fal ml-1" :class="sortIcon('date')"></i>
            </th>
            <th>Bezocht door</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody v-for="row in visibleRows" :key="row.customer.id">
          <tr
            class="customer-row"
            :class="{ 'not-recent': !row.isRecent }"
            @click="toggleExpand(row.customer.id)"
          >
            <td class="expand-cell">
              <i class="fal" :class="isExpanded(row.customer.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </td>
            <td>
              <router-link :to="{ name: 'customer-details', params: { id: row.customer.id } }" @click.native.stop>
                {{ row.customer.name }}
              </router-link>
            </td>
            <td>
              <span v-if="row.lastVisit">{{ row.lastVisit.created_at | time-day }}</span>
            </td>
            <td>
              <span v-if="row.lastVisit && row.lastVisit.user">
                {{ row.lastVisit.user.firstname }} {{ row.lastVisit.user.lastname }}
              </span>
            </td>
            <td>
              <span v-if="row.isRecent" class="badge badge-success">Recent bezocht</span>
              <span v-else class="badge badge-warning">Niet recent bezocht</span>
            </td>
          </tr>
          <template v-if="isExpanded(row.customer.id)">
            <tr v-if="row.allVisits.length === 0" class="visit-row">
              <td></td>
              <td colspan="4" class="text-muted pl-4">Geen bezoeken gevonden</td>
            </tr>
            <tr
              v-for="visit in row.allVisits"
              :key="visit.id"
              class="visit-row"
            >
              <td></td>
              <td class="pl-4 text-muted"><i class="fal fa-calendar-check mr-2"></i></td>
              <td>{{ visit.created_at | time-day }}</td>
              <td>
                <span v-if="visit.user">{{ visit.user.firstname }} {{ visit.user.lastname }}</span>
              </td>
              <td></td>
            </tr>
          </template>
        </tbody>
      </table>
    </template>
  </portlet>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { Can } from '../../../permission/ability.js';
import { RIGHTS } from '../roles/rights.js';

export default {
  data() {
    return {
      recentThresholdWeeks: 4,
      inactiveThresholdWeeks: 0,
      configLoaded: false,
      sortField: 'name',
      sortDesc: false,
      activeFilter: true,
      expandedIds: []
    };
  },
  computed: {
    ...mapGetters('customers', ['getCustomers']),
    ...mapGetters('customerContactMoments', ['getCustomerContactMoments']),
    ...mapGetters('portalConfig', ['getConfigByDepartment']),
    deptConfig: function() {
      var dept = this.$store.state.users.me.selectedDepartment;
      return this.getConfigByDepartment(dept);
    },
    contactMomentsByCustomer() {
      return this.getCustomerContactMoments.reduce((map, m) => {
        if (!map[m.customerId]) map[m.customerId] = [];
        map[m.customerId].push(m);
        return map;
      }, {});
    },
    isAdmin() {
      return Can(RIGHTS.IS_ADMIN);
    },
    allRows() {
      return this.getCustomers.map(customer => {
        const isActive = customer.activeCustomer !== false;
        const allVisits = (this.contactMomentsByCustomer[customer.id] || [])
          .filter(m => m.customerId === customer.id)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const lastVisit = allVisits.length ? allVisits[0] : null;
        let isRecent;
        if (!lastVisit) {
          isRecent = false;
        } else if (isActive) {
          isRecent = moment().diff(moment(lastVisit.created_at), 'weeks') < this.recentThresholdWeeks;
        } else {
          isRecent = this.inactiveThresholdWeeks > 0
            ? moment().diff(moment(lastVisit.created_at), 'weeks') < this.inactiveThresholdWeeks
            : false;
        }
        return { customer, allVisits, lastVisit, isRecent };
      });
    },
    visibleRows() {
      let rows = this.allRows.filter(r => !r.isRecent);

      if (this.activeFilter !== null) {
        rows = rows.filter(r =>
          this.activeFilter ? r.customer.activeCustomer !== false : r.customer.activeCustomer === false
        );
      }

      return rows.sort((a, b) => {
        if (this.sortField === 'name') {
          const valA = a.customer.name || '';
          const valB = b.customer.name || '';
          return this.sortDesc ? valB.localeCompare(valA) : valA.localeCompare(valB);
        } else {
          const valA = a.lastVisit ? new Date(a.lastVisit.created_at).getTime() : 0;
          const valB = b.lastVisit ? new Date(b.lastVisit.created_at).getTime() : 0;
          return this.sortDesc ? valB - valA : valA - valB;
        }
      });
    }
  },
  watch: {
    deptConfig: {
      immediate: true,
      handler: function(config) {
        if (!this.configLoaded) {
          if (config) {
            this.recentThresholdWeeks = config.recentThresholdWeeks != null ? config.recentThresholdWeeks : 4;
            this.inactiveThresholdWeeks = config.inactiveThresholdWeeks != null ? config.inactiveThresholdWeeks : 0;
            this.configLoaded = true;
          } else {
            this.$nextTick(() => {
              if (!this.configLoaded) this.configLoaded = true;
            });
          }
        }
      }
    },
    recentThresholdWeeks: function(val) {
      if (this.isAdmin && this.configLoaded && val > 0) this.saveConfig();
    },
    inactiveThresholdWeeks: function(val) {
      if (this.isAdmin && this.configLoaded && val >= 0) this.saveConfig();
    }
  },
  methods: {
    saveConfig: function() {
      var dept = this.$store.state.users.me.selectedDepartment;
      var patch = {};
      patch[dept] = {
        recentThresholdWeeks: this.recentThresholdWeeks,
        inactiveThresholdWeeks: this.inactiveThresholdWeeks,
      };
      this.$store.commit('setLoading', true);
      this.$store.dispatch('portalConfig/patch', patch).then(function() {
        this.$store.commit('setLoading', false);
      }.bind(this)).catch(function() {
        this.$store.commit('setLoading', false);
      }.bind(this));
    },
    setSort(field) {
      if (this.sortField === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortField = field;
        this.sortDesc = field === 'date';
      }
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'fa-sort';
      return this.sortDesc ? 'fa-sort-amount-down' : 'fa-sort-amount-up';
    },
    toggleExpand(id) {
      const idx = this.expandedIds.indexOf(id);
      if (idx === -1) {
        this.expandedIds.push(id);
      } else {
        this.expandedIds.splice(idx, 1);
      }
    },
    isExpanded(id) {
      return this.expandedIds.includes(id);
    }
  },
  created() {}
};
</script>

<style lang="scss" scoped>
$primary: #25b7bc;

.threshold-append {
  background-color: $primary;
  border-color: $primary;
  color: #fff;
  white-space: nowrap;
  min-width: 120px;
  justify-content: center;
}

.visits-overview-table {
  th.sortable {
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .customer-row {
    cursor: pointer;

    &.not-recent {
      background-color: #fff8e1;

      &:hover {
        background-color: darken(#fff8e1, 3%);
      }
    }

    &:not(.not-recent):hover {
      background-color: #f5f5f5;
    }
  }

  .expand-cell {
    color: #8d97ad;
    width: 32px;
  }

  .visit-row {
    background-color: #f9f9f9;
    font-size: 0.9em;
    color: #555;

    td {
      border-top: none;
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }
}
</style>
